// Pointer input for a fixed scene: taps only. REFOCUS-2026-09.md §4 makes the
// game "mobile portrait, fixed scene… no pan or zoom", so the camera is fitted
// for the player and the only gesture left is pointing at something.
//
// Uses Pointer Events so mouse and touch share one code path. A press that
// barely moves and barely lasts is a tap; a press that holds is a long-press
// context request, which is how a right-click behaves on a phone.

import type { Point } from './iso';

export interface TapInputOptions {
  /** Fired for a press-and-release that did not turn into a long-press. */
  onTap?: (screen: Point) => void;
  /** Fired for a held press, or a right-click. */
  onContextTap?: (screen: Point) => void;
  /** Movement in CSS px before a press stops counting as a tap. */
  moveThreshold?: number;
  /** Hold time in ms before a press counts as a long-press. */
  holdMs?: number;
}

export class TapInput {
  private pointerId: number | null = null;
  private startX = 0;
  private startY = 0;
  private held = false;
  private detach: Array<() => void> = [];

  constructor(
    private element: HTMLElement,
    private options: TapInputOptions = {},
  ) {
    this.attach();
  }

  private attach(): void {
    const moveThreshold = this.options.moveThreshold ?? 10;
    const holdMs = this.options.holdMs ?? 300;
    let holdTimer: ReturnType<typeof setTimeout> | null = null;

    const clearHold = (): void => {
      if (holdTimer !== null) clearTimeout(holdTimer);
      holdTimer = null;
    };

    const onPointerDown = (e: PointerEvent): void => {
      // The right button drives onContextTap on release instead; letting it also
      // start tap tracking made a right-click register as a left tap too.
      if (e.button === 2) return;
      // One pointer at a time: a second finger is not a gesture any more, it is
      // someone trying to pinch, and the scene does not move.
      if (this.pointerId !== null) return;
      this.pointerId = e.pointerId;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.held = false;
      this.element.setPointerCapture(e.pointerId);
      clearHold();
      holdTimer = setTimeout(() => {
        this.held = true;
      }, holdMs);
    };

    const onPointerMove = (e: PointerEvent): void => {
      if (e.pointerId !== this.pointerId) return;
      if (this.held) return;
      const moved = Math.hypot(e.clientX - this.startX, e.clientY - this.startY);
      // Past the threshold the finger was tracing something, not pointing at
      // it. A long-press from here would fire on a slide the player never
      // meant as a hold.
      if (moved >= moveThreshold) {
        clearHold();
        this.held = true;
      }
    };

    const release = (e: PointerEvent, cancelled: boolean): void => {
      if (e.pointerId !== this.pointerId) return;
      clearHold();
      this.pointerId = null;
      if (cancelled) return;
      if (this.held) this.options.onContextTap?.({ x: e.clientX, y: e.clientY });
      else this.options.onTap?.({ x: e.clientX, y: e.clientY });
    };

    const onPointerUp = (e: PointerEvent): void => release(e, false);
    const onPointerCancel = (e: PointerEvent): void => release(e, true);

    const onContextMenu = (e: MouseEvent): void => {
      // The browser's own menu never belongs on the game canvas; the radial
      // menu (wired by the caller) is the one a player should see instead.
      e.preventDefault();
      this.options.onContextTap?.({ x: e.clientX, y: e.clientY });
    };

    this.element.addEventListener('pointerdown', onPointerDown);
    this.element.addEventListener('pointermove', onPointerMove);
    this.element.addEventListener('pointerup', onPointerUp);
    this.element.addEventListener('pointercancel', onPointerCancel);
    this.element.addEventListener('contextmenu', onContextMenu);

    this.detach = [
      () => this.element.removeEventListener('pointerdown', onPointerDown),
      () => this.element.removeEventListener('pointermove', onPointerMove),
      () => this.element.removeEventListener('pointerup', onPointerUp),
      () => this.element.removeEventListener('pointercancel', onPointerCancel),
      () => this.element.removeEventListener('contextmenu', onContextMenu),
      () => clearHold(),
    ];
  }

  destroy(): void {
    for (const off of this.detach) off();
    this.detach = [];
    this.pointerId = null;
  }
}
